import { useEffect, useState } from 'preact/hooks';
import type { EpisodeDto } from '../../shared/types';
import { api, ApiError } from '../api';
import { Artwork } from '../components/Artwork';
import { DownloadButton } from '../components/DownloadButton';
import { SkeletonHeader } from '../components/Skeleton';
import { TranscriptPanel } from '../components/TranscriptPanel';
import { formatDate } from '../lib/intl';
import { sanitizeShowNotesHtml } from '../lib/sanitize';
import { currentPath } from '../router';
import { formatTime, playEpisode, seekTo } from '../state/player';
import { addToQueue } from '../state/queue';
import { isSignedIn } from '../state/session';

export function Episode({ id }: { id: string }) {
  const episodeId = Number.parseInt(id, 10);
  const [episode, setEpisode] = useState<EpisodeDto | null>(null);
  const [fullContent, setFullContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [copiedBite, setCopiedBite] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Clear out whatever the previous episode id left behind — otherwise a failed load's
    // error stays on screen underneath (and then over) a subsequent id's successful one,
    // since the render below shows the error whenever `error` is set, regardless of id.
    setError(null);
    api
      .episode(episodeId)
      .then((res) => {
        if (cancelled) return;
        setEpisode(res.episode);
        setFullContent(res.fullContentHtml);

        // Check if a timestamp was linked in the query string
        const params = new URLSearchParams(currentPath.value.split('?')[1] ?? '');
        const tParam = params.get('t') ?? params.get('start');
        if (tParam) {
          const startTime = Number.parseFloat(tParam);
          if (Number.isFinite(startTime) && startTime >= 0) {
            void playEpisode(res.episode, { startAt: startTime }).then(() => {
              seekTo(startTime);
            });
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load the episode.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [episodeId]);

  if (loading) {
    return (
      <div class="view">
        <SkeletonHeader label="Loading episode" />
      </div>
    );
  }
  if (error || !episode) return <p class="error">{error ?? 'Episode not found.'}</p>;

  const resumeAt = episode.progress?.positionSec ?? 0;

  const handleCopyBite = (startTime: number) => {
    const url = new URL(`/episode/${episode.id}?t=${startTime}`, location.origin).toString();
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedBite(startTime);
      setTimeout(() => setCopiedBite(null), 2000);
    });
  };

  return (
    <div class="view">
      <header class="episode-header">
        {/*
          The other half of the shared-element morph: the list row that linked here names its
          thumbnail `art-<id>` too (see EpisodeRow), so the browser interpolates the small
          rectangle into this large one across the navigation rather than cross-fading two
          unrelated images. The name is on a wrapper because it has to survive the artwork
          falling back to its placeholder.
        */}
        <div class="episode-header__art-morph" style={{ viewTransitionName: `art-${episode.id}` }}>
          <Artwork
            src={episode.imageUrl ?? episode.feedImageUrl}
            alt=""
            size={140}
            className="episode-header__art"
          />
        </div>
        <div>
          <a class="episode-header__show" href={`/podcast/${episode.feedId}`}>
            {episode.feedTitle}
          </a>
          <h1>{episode.title}</h1>
          <p class="muted">
            {episode.pubDate && formatDate(episode.pubDate)}
            {episode.durationSec ? ` · ${formatTime(episode.durationSec)}` : ''}
            {episode.episodeType && episode.episodeType !== 'full' ? ` · ${episode.episodeType}` : ''}
          </p>

          <div class="episode-header__actions">
            <button type="button" class="button" onClick={() => playEpisode(episode)}>
              {resumeAt > 5 ? `Resume at ${formatTime(resumeAt)}` : 'Play'}
            </button>
            {isSignedIn.value && (
              <button
                type="button"
                class="button button--ghost"
                // Same optimistic path as the list rows, so the two entry points to the
                // queue behave identically and both announce themselves.
                onClick={() => void addToQueue(episode)}
              >
                Add to queue
              </button>
            )}
            <DownloadButton episode={episode} variant="button" />
          </div>
        </div>
      </header>

      {episode.soundbites.length > 0 && (
        <section class="section">
          <h2 class="section__title">Soundbites</h2>
          <ul class="soundbites">
            {episode.soundbites.map((bite) => (
              <li key={bite.startTime} class="soundbite-item">
                <button
                  type="button"
                  class="chip"
                  onClick={async () => {
                    await playEpisode(episode, { startAt: bite.startTime });
                    seekTo(bite.startTime);
                  }}
                >
                  {bite.title ?? `Clip at ${formatTime(bite.startTime)}`} ·{' '}
                  {formatTime(bite.duration)}
                </button>
                <button
                  type="button"
                  class="icon-button icon-button--small"
                  title="Copy clip link"
                  aria-label={`Copy link to ${bite.title ?? 'clip'}`}
                  onClick={() => handleCopyBite(bite.startTime)}
                >
                  {copiedBite === bite.startTime ? '✓' : '🔗'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {episode.persons.length > 0 && (
        <section class="section">
          <h2 class="section__title">Credits</h2>
          <ul class="credits">
            {episode.persons.map((person) => (
              <li key={`${person.name}-${person.role ?? ''}`} class="credits__item">
                {person.img && <Artwork src={person.img} alt="" size={32} />}
                <span>
                  <a href={`/person/${encodeURIComponent(person.name)}`} class="credits__link">
                    <strong>{person.name}</strong>
                  </a>
                  {person.role && <span class="muted"> · {person.role}</span>}
                  {person.href && (
                    <a
                      href={person.href}
                      target="_blank"
                      rel="external noopener noreferrer"
                      class="muted"
                      style={{ marginLeft: '0.35rem', fontSize: '0.78rem' }}
                      title="External profile"
                    >
                      ↗
                    </a>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section class="section">
        <h2 class="section__title">Show notes</h2>
        <div
          class="prose"
          // Publisher HTML, sanitized to a safe subset — see src/client/lib/sanitize.ts.
          dangerouslySetInnerHTML={{
            __html: sanitizeShowNotesHtml(
              fullContent ?? episode.description ?? '<p>No show notes.</p>',
            ),
          }}
        />
      </section>

      {episode.transcripts.length > 0 && (
        <section class="section">
          <h2 class="section__title">Transcript</h2>
          <TranscriptPanel episodeId={episode.id} />
        </section>
      )}
    </div>
  );
}
